import React from "react";
import {
  XCircleIcon,
  PlusCircleIcon,
  CheckCircleFilledIcon,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import {
  BRAND_AVANTI,
  BRAND_STAYSURE,
  CLASSIC,
  COMPREHENSIVE,
} from "@/constants";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { CancellationCovers } from "@/schema/quote-page/page.schema";
import { DynamicCoverLimitProduct } from "@/types/DynamicCoverLimitProductType";

import Styles from "./comparison-table-cell.module.css";

export type ComparisionTableCellProps = {
  cover:
    | "basic"
    | "comprehensive"
    | "signature"
    | "essentials"
    | "classic"
    | "deluxe";
  type: "included" | "notIncluded" | "extra" | "none";
  label: string;
};

function renderIcon(type: string) {
  if (type === "included") {
    return (
      <div className="mb-[6px]">
        <CheckCircleFilledIcon
          fill="#008205"
          width={24}
          height={24}
          stroke="#FFFFFF"
        />
      </div>
    );
  }
  if (type === "notIncluded") {
    return (
      <div className={`mb-[6px] ${Styles.xCircleSvg}`}>
        <XCircleIcon strokeWidth={0} />
      </div>
    );
  }
  if (type === "extra") {
    return (
      <div className="mb-[6px]">
        <PlusCircleIcon fill="#000000" width={24} height={24} />
      </div>
    );
  }
  return null;
}

export function BasicCell({ cover, type, label }: ComparisionTableCellProps) {
  return (
    <div
      className={`pt-[12px] md:w-[119px] ${
        type === "none" ? "sm:h-[33px]" : "sm:h-[63px]"
      } sm:w-[96px] mt-[-4px]`}
    >
      <div
        className={`sm:w-[82px] ${
          type === "none" ? "sm:h-[21px]" : "sm:h-[51px]"
        } grid justify-items-center`}
      >
        {renderIcon(type)}
        <span className="text-center leading-[21px] text-[14px] font-semibold-font font-semibold text-text-functional">
          {label}
        </span>
        <div
          className={`md:w-[119px] sm:w-[96px] sm:h-[4px] basic ${cover} ${Styles.cellBottomDividerColor} border-r-2 border-white  mt-[8px]`}
        />
      </div>
    </div>
  );
}

export function ComprehensiveCell({
  cover,
  type,
  label,
}: ComparisionTableCellProps) {
  return (
    <div
      className={`pt-[12px] md:w-[152px] ${
        type === "none" ? "sm:h-[33px]" : "sm:h-[63px]"
      } sm:w-[120px] mt-[-4px]`}
    >
      <div
        className={`sm:w-[82px] ${
          type === "none" ? "sm:h-[21px]" : "sm:h-[51px]"
        } grid justify-items-center`}
      >
        {renderIcon(type)}
        <span className="text-center leading-[21px] text-[14px] font-semibold-font font-semibold text-text-functional">
          {label}
        </span>
        <div
          className={`md:w-[152px] sm:w-[120px] sm:h-[4px] ${cover} ${Styles.cellBottomDividerColor}  border-r border-white mt-[8px]`}
        />
      </div>
    </div>
  );
}

export function SignatureCell({
  cover,
  type,
  label,
}: ComparisionTableCellProps) {
  return (
    <div
      className={`pt-[12px] md:w-[119px] ${
        type === "none" ? "sm:h-[33px]" : "sm:h-[63px]"
      } sm:w-[96px] mt-[-4px]`}
    >
      <div
        className={`sm:w-[82px] ${
          type === "none" ? "sm:h-[21px]" : "sm:h-[51px]"
        } grid justify-items-center`}
      >
        {renderIcon(type)}
        <span className="text-center leading-[21px] text-[14px] font-semibold-font font-semibold text-text-functional">
          {label}
        </span>
        <div
          className={`md:w-[119px] sm:w-[96px] sm:h-[4px] ${cover} ${Styles.cellBottomDividerColor} border-l border-white mt-[8px]`}
        />
      </div>
    </div>
  );
}

export function TwoColumnLeftCell({
  cover,
  type,
  label,
}: ComparisionTableCellProps) {
  const { current } = useWizard();
  return (
    <div
      className={`pt-[8px] ${
        current.brandSpecificParams?.isEnableComparisonTableStyle
          ? "w-[156px]"
          : "sm:w-[156px] md:w-[196px] "
      } ${type === "none" ? "sm:h-[33px]" : "sm:h-[63px]"}`}
    >
      <div
        className={`sm:w-[82px] ${
          type === "none" ? "sm:h-[21px]" : "sm:h-[51px]"
        } grid justify-items-center`}
      >
        {renderIcon(type)}
        <span className="text-center leading-[21px] text-[14px] font-semibold-font font-semibold text-text-functional">
          {label}
        </span>
        <div
          className={`${
            current.brandSpecificParams?.isEnableComparisonTableStyle
              ? "w-[156px]"
              : "sm:w-[156px] md:w-[196px]"
          } sm:h-[4px] ${cover} ${
            Styles.cellBottomDividerColor
          }  border-l border-white mt-[8px]`}
        />
      </div>
    </div>
  );
}

export function TwoColumnRightCell({
  cover,
  type,
  label,
}: ComparisionTableCellProps) {
  const { current } = useWizard();
  return (
    <div
      className={`pt-[8px]  ${
        current.brandSpecificParams?.isEnableComparisonTableStyle
          ? "w-[156px]"
          : "sm:w-[156px] md:w-[196px] "
      } ${type === "none" ? "sm:h-[33px]" : "sm:h-[63px]"}`}
    >
      <div
        className={`sm:w-[82px] ${
          type === "none" ? "sm:h-[21px]" : "sm:h-[51px]"
        } grid justify-items-center`}
      >
        {renderIcon(type)}
        <span className="text-center leading-[21px] text-[14px] font-semibold-font font-semibold text-text-functional">
          {label}
        </span>
        <div
          className={`${
            current.brandSpecificParams?.isEnableComparisonTableStyle
              ? "w-[156px]"
              : " sm:w-[156px] md:w-[194px] "
          } sm:h-[4px] ${cover} ${
            Styles.cellBottomDividerColor
          } border-l border-white mt-[8px]`}
        />
      </div>
    </div>
  );
}

export function MobileComparisonCell({
  cover,
  label,
  isCancellation,
  setCancellationCoverOpen,
  policyPackage,
  setCancelltionChangeCoverType,
  setDynamicCoverLimit,
  dynamicCoverLimit,
  brand,
}: Readonly<
  ComparisionTableCellProps & {
    isCancellation?: boolean;
    dynamicCoverLimit: DynamicCoverLimitProduct | null;
    setCancellationCoverOpen?: (val: boolean) => void;
    policyPackage?: string;
    setCancelltionChangeCoverType?: (val: string) => void;
    setDynamicCoverLimit?: (val: CancellationCovers[] | null) => void;
    brand?: "staysure" | "avanti" | undefined;
  }
>) {
  const { t } = useWizardTranslation();

  const openModal = () => {
    if (setCancellationCoverOpen) setCancellationCoverOpen(true);
    if (setDynamicCoverLimit)
      setDynamicCoverLimit(dynamicCoverLimit?.values ?? null);
    if (setCancelltionChangeCoverType)
      setCancelltionChangeCoverType(policyPackage ?? "");
  };

  const shouldRenderChangeButton =
    isCancellation &&
    dynamicCoverLimit?.values &&
    dynamicCoverLimit?.values.length > 1 &&
    (brand === BRAND_STAYSURE ||
      policyPackage?.toUpperCase() !== COMPREHENSIVE);

  return (
    <div
      className={`pt-[8px] ${
        isCancellation ? "sm:h-[63px]" : ""
      } mt-[-4px] flex-1 h-full`}
    >
      <div
        className={`grid ${
          isCancellation ? "h-full grid-rows-[auto_1fr_auto]" : ""
        }`}
      >
        <span
          className={`leading-[21px] font-semibold-font font-normal ${
            brand === BRAND_AVANTI
              ? "!text-surface-neutral-option-9 text-[16px]"
              : "text-black text-[14px]"
          }`}
        >
          {label}
        </span>
        <div>
          {shouldRenderChangeButton && (
            <div>
              <LabelButtonWrapper
                hierarchy="tertiary"
                label={t("change_button", "Change")}
                className="text-[12px] font-body-font font-[600] !px-0 leading-[19.49px] h-[17px]"
                onClick={openModal}
              />
            </div>
          )}
        </div>

        <div
          className={`w-[1/3] sm:h-[2px] ${cover} ${
            policyPackage?.toUpperCase() === CLASSIC && brand === BRAND_AVANTI
              ? "!bg-surface-accent-4-primary"
              : Styles.cellBottomDividerColorMobile
          } border-l border-white mt-[8px]`}
        />
      </div>
    </div>
  );
}
